import { CustomIcons, Link, Text } from '@ltvco/refresh-lib/theme';
import { SearchData, SearchTabData } from '@ltvco/refresh-lib/v1';
import {
  AttachMoneyRounded,
  AutoAwesomeRounded,
  GavelRounded,
  LightbulbRounded,
  MapRounded,
  PersonRounded,
  RadarRounded,
} from '@mui/icons-material';
import AlternateEmailFilled from '@mui/icons-material/AlternateEmail';
import DriveEtaFilled from '@mui/icons-material/DriveEta';
import EmailFilled from '@mui/icons-material/Email';
import HomeFilled from '@mui/icons-material/Home';
import PhoneFilled from '@mui/icons-material/Phone';
import React, { ReactElement } from 'react';

import { LEGAL_INTELLIGENCE_ROUTES } from '../CourtCases/constants';

import { StyledSubheaderText, StyledSubheaderTextPhone } from './components';
import { EmailSearch } from './Email/EmailSearch';
import { PersonSearch } from './Person/PersonSearch';
import { PhoneSearch } from './Phone/PhoneSearch';
import { PropertySearch } from './Property/PropertySearch';
import { UnclaimedMoneySearch } from './UnclaimedMoney/UnclaimedMoneySearch';
import { UsernameSearch } from './Username/UsernameSearch';
import { VehicleSearch } from './Vehicle/VehicleSearch';

export interface SearchTabType {
  id: string;
  displayName: string;
  icon: React.ReactElement;
  subheaderText: string;
  href?: string;
}

export interface SearchProps {
  isMobile: boolean;
  placeholderText: string;
  // eslint-disable-next-line no-unused-vars
  onSearchSuccess: (data: SearchData) => void;
  placement: string;
}

export interface SubheaderTextProps {
  subheaderText: string;
  /** Enables multiline / icon layout tuned for the long phone disclaimer */
  isPhoneTab?: boolean;
  isVehicleTab?: boolean;
  // eslint-disable-next-line no-unused-vars
  setIsVINModalOpen?: (isOpen: boolean) => void;
}

export const getUnclaimedMoneyTab = (): SearchTabData => ({
  displayName: 'Unclaimed Money',
  id: 'unclaimedMoneyTab',
  icon: (
    <AttachMoneyRounded
      fontSize="small"
      sx={{ color: { xs: '#0052FF', md: '#0A0B0D' } }}
    />
  ),
});

export const getDashboardUpdateTabs = (): SearchTabData[] => {
  return [
    {
      displayName: 'Deep Search',
      id: 'deepSearchTab',
      href: '/chatbv/chat',
    },
    {
      displayName: 'Unclaimed Money',
      id: 'unclaimedMoneyTab',
      href: '/dashboard/unclaimed-money',
    },
    {
      displayName: 'Dark Web Scan',
      id: 'darkWebScanTab',
      href: '/dashboard/dark-web',
    },
    {
      displayName: 'Neighborhood Search',
      id: 'neighborhoodSearchTab',
      href: '/dashboard/neighborhood-report',
    },
    {
      displayName: 'Fraud Scan',
      id: 'fraudScanTab',
      href: '/dashboard/fraud',
    },
    {
      displayName: 'Court Cases',
      id: 'courtCasesTab',
      href: LEGAL_INTELLIGENCE_ROUTES.BASE,
    },
    {
      displayName: 'Patent Search',
      id: 'patentSearchTab',
      href: '/dashboard/patent-search',
    },
  ];
};

export const decorateSearchTabs = (searchTabs: any): SearchTabType[] => {
  const subHeaderTextByTab = {
    peopleTab:
      'Use the last known city or state where a person resided to help narrow results.',
    phoneTab:
      'Enter a phone number to search for a possible owner, associated address, carrier details, photos, additional contact info and much more. Phone number search results are powered by a third-party data provider. Coverage varies by country, and not all phone numbers will return a match.',
    propertyTab:
      'Enter a full property address to access ownership details, deed records, tax history, and more.',
    emailTab:
      'Enter an email address to search for a possible owner, photos, social networks and more.',
    usernameTab:
      'Enter a username or social media profile to see information about the potential owner.',
    vehicleTab:
      'Search by VIN, License Plate, or Make to get a full vehicle history report.',
    unclaimedMoneyTab:
      'There’s an estimated $70 billion worth of unclaimed money in the United States.',
  };

  const iconByTab = {
    peopleTab: <PersonRounded fontSize="small" />,
    phoneTab: <PhoneFilled fontSize="small" />,
    propertyTab: <HomeFilled fontSize="small" />,
    emailTab: <EmailFilled fontSize="small" />,
    usernameTab: <AlternateEmailFilled fontSize="small" />,
    vehicleTab: <DriveEtaFilled fontSize="small" />,
    deepSearchTab: <AutoAwesomeRounded fontSize="small" />,
    unclaimedMoneyTab: <AttachMoneyRounded fontSize="small" />,
    darkWebScanTab: <CustomIcons.DarkWebIcon fontSize="small" />,
    neighborhoodSearchTab: <MapRounded fontSize="small" />,
    fraudScanTab: <RadarRounded fontSize="small" />,
    courtCasesTab: <GavelRounded fontSize="small" />,
    patentSearchTab: <LightbulbRounded fontSize="small" />,
  };

  return searchTabs.map((tab: any) => ({
    id: tab.id,
    displayName: tab.displayName,
    icon: iconByTab[tab.id as keyof typeof iconByTab],
    subheaderText:
      subHeaderTextByTab[tab.id as keyof typeof subHeaderTextByTab],
    href: tab?.href ?? '',
  }));
};

export const getPlaceholderText = (
  activeTab: string,
  isMobile?: boolean
): string => {
  const placeholderText = {
    peopleTab: 'e.g. John Smith in New York, NY Age 45',
    phoneTab: 'e.g. (123)456-7890 ',
    propertyTab: 'e.g. 123 Main St. New York, NY',
    emailTab: 'e.g. jsmith@example.com',
    usernameTab: 'e.g. beenverified',
    vehicleTab: 'e.g. 1g1ze51234567',
    unclaimedMoneyTab: 'e.g. John Smith',
  };

  const placeholderTextMobile = {
    peopleTab: 'Person',
    phoneTab: 'Phone',
    propertyTab: 'Property',
    emailTab: 'Email',
    usernameTab: 'Username',
    vehicleTab: 'VIN',
    unclaimedMoneyTab: 'Full Name',
  };

  const placeholderMap = isMobile ? placeholderTextMobile : placeholderText;
  return placeholderMap[activeTab as keyof typeof placeholderMap];
};

export const handleFormSubmit = (
  event: React.FormEvent,
  searchValue: string,
  activeTab: string,
  // eslint-disable-next-line no-unused-vars
  onSearchSuccess: (data: SearchData) => void
) => {
  event.preventDefault();

  if (!searchValue.trim()) {
    return;
  }

  // This will be replaced with actual API call implementation
  // Mock success data for testing
  const mockData = {
    reportType: activeTab,
    searchParams: `query=${encodeURIComponent(searchValue)}`,
    permalink: 'permalink1',
  };

  onSearchSuccess(mockData);
};

export const renderSearchForm = (
  activeTab: string = '',
  placeholderText: string,
  // eslint-disable-next-line no-unused-vars
  onSearchSuccess: (data: SearchData) => void,
  placement: string = 'dashboard',
  isMobile: boolean
): ReactElement | null => {
  if (activeTab === 'peopleTab') {
    return (
      <PersonSearch
        placement={placement}
        isMobile={isMobile}
        placeholderText={placeholderText}
        onSearchSuccess={onSearchSuccess}
      />
    );
  }

  if (activeTab === 'propertyTab') {
    return (
      <PropertySearch
        placement={placement}
        isMobile={isMobile}
        placeholderText={placeholderText}
        onSearchSuccess={onSearchSuccess}
      />
    );
  }

  if (activeTab === 'phoneTab') {
    return (
      <PhoneSearch
        placement={placement}
        isMobile={isMobile}
        placeholderText={placeholderText}
        onSearchSuccess={onSearchSuccess}
      />
    );
  }

  if (activeTab === 'usernameTab') {
    return (
      <UsernameSearch
        placement={placement}
        isMobile={isMobile}
        placeholderText={placeholderText}
        onSearchSuccess={onSearchSuccess}
      />
    );
  }

  if (activeTab === 'emailTab') {
    return (
      <EmailSearch
        placement={placement}
        isMobile={isMobile}
        placeholderText={placeholderText}
        onSearchSuccess={onSearchSuccess}
      />
    );
  }

  if (activeTab === 'vehicleTab') {
    return (
      <VehicleSearch
        placement={placement}
        isMobile={isMobile}
        placeholderText={placeholderText}
        onSearchSuccess={onSearchSuccess}
      />
    );
  }

  if (activeTab === 'unclaimedMoneyTab') {
    return (
      <UnclaimedMoneySearch
        placement={placement}
        isMobile={isMobile}
        placeholderText={placeholderText}
        onSearchSuccess={onSearchSuccess}
      />
    );
  }

  return null;
};

export const SubheaderText = ({
  subheaderText,
  isPhoneTab = false,
  isVehicleTab,
  setIsVINModalOpen,
}: SubheaderTextProps) => {
  if (isVehicleTab && setIsVINModalOpen) {
    return (
      <StyledSubheaderText variant="body2">
        <LightbulbRounded fontSize="small" color="inherit" />
        <Text fontSize="small">
          {subheaderText}{' '}
          <Link
            sx={{
              cursor: 'pointer',
              color: 'inherit',
              textDecorationColor: 'inherit',
            }}
            onClick={() => setIsVINModalOpen(true)}
          >
            Where
          </Link>{' '}
          can I find my VIN?
        </Text>
      </StyledSubheaderText>
    );
  }

  if (isPhoneTab) {
    return (
      <StyledSubheaderTextPhone variant="body2">
        <LightbulbRounded
          fontSize="small"
          color="inherit"
          sx={{ flexShrink: 0 }}
        />
        <Text
          component="span"
          variant="body2"
          sx={{ flex: '1 1 0', minWidth: 0, display: 'block' }}
        >
          {subheaderText}
        </Text>
      </StyledSubheaderTextPhone>
    );
  }

  return (
    <StyledSubheaderText variant="body2">
      <LightbulbRounded fontSize="small" color="inherit" />
      {subheaderText}
    </StyledSubheaderText>
  );
};
